import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { acceptAnalyticsConsent, rejectAnalyticsConsent } from "../../observability";

const STORAGE_KEY = "worthify_cookie_consent";

/**
 * GDPR / UK PECR-compliant cookie banner.
 *
 * Worthify uses two storage tiers:
 *   1. Essential: localStorage + Stripe session (always on, no consent needed)
 *   2. Analytics: PostHog product analytics (REQUIRES explicit opt-in)
 *
 * The banner offers a genuine choice — "Accept analytics" and "Essential only"
 * are visually equal so the design satisfies the ICO's "no dark patterns" rule.
 * Sentry error tracking runs regardless (legitimate interest — debugging
 * crashes that the user themselves triggered) with PII off + Replay maskAllText.
 */
export const CookieBanner = ({ language = "en" }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = (() => {
      try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    if (seen) return;
    const id = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(id);
  }, []);

  const handleAccept = () => {
    acceptAnalyticsConsent();
    setVisible(false);
  };

  const handleReject = () => {
    rejectAnalyticsConsent();
    setVisible(false);
  };

  const copy = language === "pl"
    ? {
        title: "Pomóż nam ulepszyć Worthify",
        body: "Używamy plików niezbędnych (localStorage + sesja Stripe) zawsze. Z Twoją zgodą włączamy też analitykę produktu (PostHog), aby zobaczyć które funkcje są używane i poprawić lejek. Brak marketingu, brak sprzedaży danych.",
        accept: "Akceptuję analitykę",
        reject: "Tylko niezbędne",
        learn: "Polityka cookies",
      }
    : {
        title: "Help us improve Worthify",
        body: "We always use essential storage (localStorage + Stripe session). With your consent we also enable product analytics (PostHog) to see which features are used and improve the funnel. No marketing, no data selling.",
        accept: "Accept analytics",
        reject: "Essential only",
        learn: "Cookie policy",
      };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] left-0 right-0 bottom-0 px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div
            className="mx-auto pointer-events-auto relative"
            style={{
              maxWidth: 720,
              borderRadius: 18,
              background: "linear-gradient(135deg,rgba(15,23,42,0.96) 0%,rgba(30,41,59,0.96) 100%)",
              border: "1px solid rgba(134,239,172,0.28)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.45)",
              backdropFilter: "blur(14px)",
              padding: "14px 16px 14px 14px",
              color: "var(--wf-modal-text)",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#22C55E,#16A34A)",
                  boxShadow: "0 6px 14px rgba(34,197,94,0.35)",
                }}
              >
                <Cookie className="w-5 h-5 text-slate-900 dark:text-white" strokeWidth={2.4} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="font-black"
                  style={{ fontSize: 13.5, letterSpacing: "-0.2px", color: "var(--wf-modal-text)" }}
                >
                  {copy.title}
                </p>
                <p
                  className="mt-1 leading-[1.45]"
                  style={{ fontSize: 12, color: "var(--wf-modal-text-muted)" }}
                >
                  {copy.body}{" "}
                  <Link
                    to="/cookies"
                    className="font-semibold underline"
                    style={{ color: "var(--pricing-prem-check)", textUnderlineOffset: 2 }}
                    data-testid="cookie-banner-policy-link"
                  >
                    {copy.learn}
                  </Link>
                  .
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleAccept}
                    data-testid="cookie-banner-accept"
                    className="font-bold inline-flex items-center justify-center"
                    style={{
                      padding: "8px 16px",
                      borderRadius: 999,
                      background: "linear-gradient(135deg,#22C55E 0%,#16A34A 100%)",
                      color: "var(--wf-modal-text)",
                      fontSize: 12.5,
                      boxShadow: "0 4px 12px rgba(34,197,94,0.32)",
                    }}
                  >
                    {copy.accept}
                  </button>
                  <button
                    type="button"
                    onClick={handleReject}
                    data-testid="cookie-banner-reject"
                    className="font-bold inline-flex items-center justify-center"
                    style={{
                      padding: "8px 16px",
                      borderRadius: 999,
                      background: "rgba(148,163,184,0.18)",
                      color: "var(--wf-modal-text-muted)",
                      fontSize: 12.5,
                      border: "1px solid rgba(148,163,184,0.32)",
                    }}
                  >
                    {copy.reject}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
